export const FilterKey = {
  year: 'year',
  facility: 'facility',
  scope: 'scope',
  subFilters: 'subFilters'
};
export const FilterLabel = {
  year: 'Year',
  facility: 'Facility',
  scope: 'Scope',
  subFilters: 'Sub Filters'
};

export const ScopeOptions = [
  {
    value: 'scope1',
    label: 'Scope 1'
  },
  {
    value: 'scope2',
    label: 'Scope 2'
  },
  {
    value: 'scope3',
    label: 'Scope 3',
    checkValid: true
  }
];

export const ModuleConstants = {
  machine: 'machine',
  vehicle: 'vehicle',
  refrigerant: 'refrigerant',
  electricity: 'electricity',
  water: 'water',
  waste: 'waste',
  capitalgoods: 'capitalgoods',
  upstreamTransportation: 'upstreamTransportation',
  downstreamTransportation: 'downstreamTransportation',
  employeeCommute: 'employeeCommute',
  businessTravel: 'businessTravel',
  capitalGoodsAverage: 'capitalGoodsAverage',
  rawMaterialAverage: 'rawMaterialAverage',
  processingOfSoldProductsAverage: 'processingOfSoldProductsAverage'
};

export const SubFiltersOptions = [
  {
    value: ModuleConstants.machine,
    label: 'By Machine',
    scope: 'scope1'
  },
  {
    value: ModuleConstants.vehicle,
    label: 'By Vehicle',
    scope: 'scope1'
  },
  {
    value: ModuleConstants.refrigerant,
    label: 'By Refrigerant',
    scope: 'scope1'
  },
  {
    value: ModuleConstants.electricity,
    label: 'Electricity',
    scope: 'scope2'
  },
  {
    value: ModuleConstants.waste,
    label: 'By Waste',
    scope: 'scope3'
  },
  {
    value: ModuleConstants.upstreamTransportation,
    label: 'Upstream Transportation',
    scope: 'scope3'
  },
  {
    value: ModuleConstants.downstreamTransportation,
    label: 'Downstream Transportation',
    scope: 'scope3'
  },
  {
    value: ModuleConstants.employeeCommute,
    label: 'Employee Commute',
    scope: 'scope3'
  },
  {
    value: ModuleConstants.businessTravel,
    label: 'Business Travel',
    scope: 'scope3'
  },
  {
    value: ModuleConstants.capitalGoodsAverage,
    label: 'Capital Goods- Average',
    scope: 'scope3'
  }
];

export const scopeToApiParam = (
  scope: string
): '1' | '2' | '3' | undefined => {
  if (scope === 'scope1') return '1';
  if (scope === 'scope2') return '2';
  if (scope === 'scope3') return '3';

  return undefined;
};

const SUB_FILTER_BY_DATA_BY = new Map(
  SubFiltersOptions.map((s) => [s.value, s])
);

const stripFacilityPrefix = (full: string, facilityName: string): string => {
  if (!facilityName) {
    return '';
  }

  const prefix = `${facilityName} - `;

  if (full.startsWith(prefix)) {
    return full.slice(prefix.length);
  }

  return '';
};

const stripSubFilterSegment = (
  full: string,
  facilityName: string,
  subFilterLabel: string
): string => {
  if (!facilityName || !subFilterLabel) {
    return '';
  }

  const pattern = `${facilityName} - ${subFilterLabel} - `;

  if (full.startsWith(pattern)) {
    return `${facilityName} - ${full.slice(pattern.length)}`;
  }

  return '';
};

const stripSubFilterPrefix = (
  value: string,
  subFilterLabel: string
): string => {
  if (!subFilterLabel) {
    return '';
  }

  const prefix = `${subFilterLabel} - `;

  if (value.startsWith(prefix)) {
    return value.slice(prefix.length);
  }

  return '';
};

const stripToDataId = (
  full: string,
  facilityName: string,
  subFilterLabel: string
): string => {
  const pattern = `${facilityName} - ${subFilterLabel} - `;

  if (full.startsWith(pattern)) {
    return full.slice(pattern.length);
  }

  const afterFacility = stripFacilityPrefix(full, facilityName);

  return stripSubFilterPrefix(afterFacility, subFilterLabel);
};

const getSubFilterLabels = (
  record: {
    dataBy?: string;
    dataByLabel?: string;
    subFilterLabel?: string;
  },
  selectedDataBy?: string
): string[] => {
  const labels = [
    record.dataByLabel,
    record.subFilterLabel,
    record.dataBy ? SUB_FILTER_BY_DATA_BY.get(record.dataBy)?.label : undefined,
    selectedDataBy
      ? SUB_FILTER_BY_DATA_BY.get(selectedDataBy)?.label
      : undefined
  ].filter((label): label is string => Boolean(label));

  return Array.from(new Set(labels));
};

const resolveDataIdWithSubFilter = (
  full: string,
  noFacility: string,
  facilityName: string,
  subFilterLabels: string[]
): string => {
  for (const subFilterLabel of subFilterLabels) {
    const fromFullPattern = stripToDataId(full, facilityName, subFilterLabel);

    if (fromFullPattern) {
      return fromFullPattern;
    }

    const fromFull = stripSubFilterPrefix(full, subFilterLabel);

    if (fromFull) {
      return fromFull;
    }

    const fromNoFacility = stripSubFilterPrefix(noFacility, subFilterLabel);

    if (fromNoFacility) {
      return fromNoFacility;
    }
  }

  return '';
};

const getGroupDisplayName = (record: {
  dataId?: string;
  dataLabel?: string;
}): string => {
  const label = record.dataLabel ?? '';
  const id = record.dataId ?? '';

  if (label && id && label !== id) {
    return `${label} - ${id}`;
  }

  return label || id;
};

export const buildGroupLabel = (
  record: {
    groupSearchFull?: string;
    groupSearchNoFacility?: string;
    dataBy?: string;
    dataByLabel?: string;
    facilityName?: string;
    subFilterLabel?: string;
    dataId?: string;
    dataLabel?: string;
  },
  ctx: {facilityId?: string; dataBy?: string}
): string => {
  const full = record.groupSearchFull ?? '';
  const noFacility = record.groupSearchNoFacility ?? '';
  const facilityName = record.facilityName ?? '';
  const displayName = getGroupDisplayName(record);
  const subFilterLabels = getSubFilterLabels(record, ctx.dataBy);

  if (ctx.dataBy) {
    if (ctx.facilityId) {
      if (displayName) {
        return displayName;
      }

      const dataIdOnly = resolveDataIdWithSubFilter(
        full,
        noFacility,
        facilityName,
        subFilterLabels
      );

      if (dataIdOnly) {
        return dataIdOnly;
      }

      return full;
    }

    if (facilityName && displayName) {
      return `${facilityName} - ${displayName}`;
    }

    for (const subFilterLabel of subFilterLabels) {
      const stripped = stripSubFilterSegment(full, facilityName, subFilterLabel);

      if (stripped) {
        return stripped;
      }
    }

    return full;
  }

  if (ctx.facilityId) {
    return noFacility;
  }

  return full;
};

export const toDecimalString = (num: number, maxDecimals = 20): string => {
  if (num === 0) return '0';

  let str = num.toString();

  // Handle exponential notation
  if (str.includes('e')) {
    const [baseStr, expStr] = str.split('e');
    const exp = parseInt(expStr, 10);

    const [integerPart, fractionalPart = ''] = baseStr.split('.');
    const digits = (integerPart + fractionalPart).replace('-', '');
    const negative = num < 0 ? '-' : '';

    let decimalPointIndex = integerPart.length + exp;

    // Limit decimal places to maxDecimals for very small numbers
    if (decimalPointIndex < -maxDecimals) {
      decimalPointIndex = -maxDecimals;
    }

    if (decimalPointIndex <= 0) {
      str = negative + '0.' + '0'.repeat(-decimalPointIndex) + digits;
    } else if (decimalPointIndex >= digits.length) {
      str = negative + digits + '0'.repeat(decimalPointIndex - digits.length);
    } else {
      str =
        negative +
        digits.slice(0, decimalPointIndex) +
        '.' +
        digits.slice(decimalPointIndex);
    }
  }

  // Trim unnecessary trailing zeros and decimal point
  str = str.replace(/\.?0+$/, '');

  return str;
};
